import React, { Suspense, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from "react-router-dom";
import Landing from "./landing/Landing.tsx";
import LoadingOverlay from "./components/LoadingOverlay.tsx";
import { AppContextProvider } from "./context/AppContextProvider";
import Contact from './landing/contact/Contact.tsx';
import { MetadataUpdater } from "./components/MetadataUpdater.tsx";
import { SentryFeedbackManager } from "./sentry/sentryFeedbackManager.ts";
import { initializeSentry } from "./sentry/sentryManager.ts";
import { ProductIdentifier } from "@common/utils/identifyProduct.ts";
import { Product } from "@common/enums/Product.ts";
import config from "./config/config.ts";

initializeSentry();
// eslint-disable-next-line react-refresh/only-export-components
const App = React.lazy(async () => await import("./App.tsx"));

// Determine the current product based on domain
const identifier = new ProductIdentifier(config.productOverride);
const currentProduct = identifier.getProductFromHost(window.location.hostname);
const isCompass = currentProduct.name === Product.Compass;

// eslint-disable-next-line react-refresh/only-export-components
const AppWrapper = () => {
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsLoading(true);

        // Use a microtask to set isLoading to false after the current render cycle
        queueMicrotask(() => { setIsLoading(false); });
    }, [location]);

    return (
        <>
            {isLoading && <LoadingOverlay />}
            <SentryFeedbackManager />
            <MetadataUpdater/>
            <Suspense fallback={<LoadingOverlay />}>
                <Routes>
                    <Route
                        path="/"
                        element={isCompass ? <Navigate to="/login" replace /> : <Landing />}
                    />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/*" element={<App />} />
                </Routes>
            </Suspense>
        </>
    );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <AppContextProvider>
            <Router>
                <AppWrapper />
            </Router>
        </AppContextProvider>
    </React.StrictMode>
);
